import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Plus, Upload, Syringe, PawPrint, ArrowLeft, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function CustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [petOpen, setPetOpen] = useState(false);
  const [editPet, setEditPet] = useState(null);
  const [vacFor, setVacFor] = useState(null);

  const load = () => api.get(`/customers/${id}/full`).then(r => setData(r.data)).catch(e => {
    toast.error(e?.response?.data?.detail || "Failed to load customer");
  });
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  const c = data.customer || {};
  const pets = Array.isArray(data.pets) ? data.pets : [];
  const invoices = Array.isArray(data.invoices) ? data.invoices : [];
  const appointments = Array.isArray(data.appointments) ? data.appointments : [];
  return (
    <div className="space-y-5" data-testid="customer-detail-page">
      <Button asChild variant="ghost" size="sm"><Link to="/customers"><ArrowLeft className="w-4 h-4 mr-1" />All customers</Link></Button>
      <div className="grid lg:grid-cols-[1fr_2fr] gap-5">
        <Card className="p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Customer</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight mt-1">{c.name}</h2>
          <div className="font-mono text-sm mt-2">{c.mobile_masked || c.mobile}</div>
          <div className="text-sm text-muted-foreground mt-1">{c.email || "—"}</div>
          <div className="text-sm text-muted-foreground mt-1">{c.address || "—"}</div>
          {c.mobile_verified && <Badge variant="secondary" className="mt-3">Mobile verified</Badge>}
          <div className="mt-5 grid grid-cols-3 gap-2 text-center">
            <KPI label="Pets" v={pets.length} />
            <KPI label="Invoices" v={invoices.length} />
            <KPI label="Appointments" v={appointments.length} />
          </div>
        </Card>
        <Card className="p-5">
          <Tabs defaultValue="pets">
            <TabsList>
              <TabsTrigger value="pets" data-testid="tab-pets">Pets</TabsTrigger>
              <TabsTrigger value="invoices" data-testid="tab-invoices">Purchase history</TabsTrigger>
              <TabsTrigger value="appts" data-testid="tab-appts">Appointments</TabsTrigger>
              <TabsTrigger value="wallet" data-testid="tab-wallet">Wallet</TabsTrigger>
              <TabsTrigger value="credit" data-testid="tab-credit">Credit Notes</TabsTrigger>
            </TabsList>
            <TabsContent value="pets" className="mt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-muted-foreground">{pets.length} pets</div>
                <Button onClick={() => { setEditPet(null); setPetOpen(true); }} data-testid="add-pet-btn"><Plus className="w-4 h-4 mr-1" />Add pet</Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {pets.map(p => (
                  <Card key={p.id} className="p-4 flex gap-3" data-testid={`pet-card-${p.id}`}>
                    {p.photo_path ? (
                      <img src={fileUrl(p.photo_path)} alt={p.name} className="w-20 h-20 rounded-lg object-cover" />
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-muted grid place-items-center text-muted-foreground"><PawPrint /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-bold text-lg">{p.name}</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-widest">{p.species} · {p.breed || "—"}</div>
                      <div className="text-xs mt-1">{p.gender || "—"}{p.weight_kg ? ` · ${p.weight_kg}kg` : ""}</div>
                      <div className="mt-2 flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => setVacFor(p)} data-testid={`vacc-${p.id}`}><Syringe className="w-3 h-3 mr-1" />Vacc.</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setEditPet(p); setPetOpen(true); }} data-testid={`edit-pet-${p.id}`}>Edit</Button>
                      </div>
                    </div>
                  </Card>
                ))}
                {pets.length === 0 && <div className="col-span-full text-sm text-muted-foreground">No pets yet.</div>}
              </div>
            </TabsContent>
            <TabsContent value="invoices" className="mt-4">
              <div className="divide-y divide-border">
                {invoices.map(i => (
                  <div key={i.id} className="py-3 flex items-center justify-between" data-testid={`inv-${i.id}`}>
                    <div>
                      <div className="font-mono text-sm font-semibold">{i.invoice_no}</div>
                      <div className="text-xs text-muted-foreground">{new Date(i.created_at).toLocaleString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading font-bold">₹{Number(i.grand_total || 0).toFixed(2)}</div>
                      <Badge variant={i.status === "paid" ? "secondary" : "destructive"} className="text-[10px]">{i.status}</Badge>
                    </div>
                  </div>
                ))}
                {invoices.length === 0 && <div className="text-sm text-muted-foreground py-3">No purchases yet.</div>}
              </div>
            </TabsContent>
            <TabsContent value="appts" className="mt-4">
              <div className="divide-y divide-border">
                {appointments.map(a => (
                  <div key={a.id} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">{new Date(a.slot_start).toLocaleString()}</div>
                      <div className="text-xs text-muted-foreground">Status: {a.status}</div>
                    </div>
                  </div>
                ))}
                {appointments.length === 0 && <div className="text-sm text-muted-foreground py-3">No appointments.</div>}
              </div>
            </TabsContent>
            <TabsContent value="wallet" className="mt-4">
              <WalletPanel customer={c} txns={data.wallet_txns || []} onChanged={load} />
            </TabsContent>
            <TabsContent value="credit" className="mt-4">
              <CreditNotesPanel customerId={id} customer={c} />
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      <PetDialog open={petOpen} onOpenChange={(v) => { setPetOpen(v); if (!v) setEditPet(null); }} customerId={id} pet={editPet} onSaved={load} />
      <VaccinationDialog open={!!vacFor} onClose={() => { setVacFor(null); load(); }} pet={vacFor} />
    </div>
  );
}

const KPI = ({ label, v }) => (
  <div className="rounded-md bg-muted px-2 py-2">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="font-heading text-xl font-black">{v}</div>
  </div>
);

function WalletPanel({ customer, txns, onChanged }) {
  const balance = Number(customer?.wallet_balance || 0);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", direction: "credit", note: "" });

  const submit = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    const signed = form.direction === "credit" ? amt : -amt;
    try {
      await api.post(`/customers/${customer.id}/wallet/adjust`, {
        amount: signed,
        note: form.note || `Manual ${form.direction}`,
      });
      toast.success(`Wallet ${form.direction === "credit" ? "credited" : "debited"} ₹${amt.toFixed(2)}`);
      setAdjustOpen(false); setForm({ amount: "", direction: "credit", note: "" });
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="wallet-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 flex-1 min-w-[200px]">
          <div className="text-[10px] uppercase tracking-widest text-emerald-900">Wallet balance</div>
          <div className="font-heading text-3xl font-bold text-emerald-900">₹{balance.toFixed(2)}</div>
        </div>
        <Button onClick={() => setAdjustOpen(true)} data-testid="wallet-adjust-btn"><Plus className="w-4 h-4 mr-1" />Manual adjustment</Button>
      </div>

      <div className="divide-y divide-border rounded-md border border-border overflow-hidden">
        {(!txns || txns.length === 0) && <div className="p-5 text-sm text-muted-foreground text-center">No wallet activity yet.</div>}
        {txns?.map((t) => (
          <div key={t.id} className="p-3 flex items-center justify-between gap-3" data-testid={`wallet-txn-${t.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className={`text-xs uppercase tracking-wider font-semibold ${t.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{t.kind || (t.amount >= 0 ? "credit" : "debit")}</span>
                <span className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString()}</span>
              </div>
              <div className="text-sm text-muted-foreground mt-0.5">{t.note || "—"}</div>
            </div>
            <div className={`font-mono font-semibold ${t.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
              {t.amount >= 0 ? "+" : ""}₹{Number(t.amount).toFixed(2)}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent className="max-w-md" data-testid="wallet-adjust-dialog">
          <DialogHeader><DialogTitle>Wallet adjustment for {customer.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Direction</Label>
              <Select value={form.direction} onValueChange={(v) => setForm((f) => ({ ...f, direction: v }))}>
                <SelectTrigger data-testid="wallet-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="credit">Credit (+) — add money to wallet</SelectItem>
                  <SelectItem value="debit">Debit (−) — remove money from wallet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Amount (₹)</Label><Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} autoFocus data-testid="wallet-amount" /></div>
            <div><Label>Note</Label><Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Reason for adjustment" data-testid="wallet-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="wallet-adjust-save">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PetDialog({ open, onOpenChange, customerId, pet, onSaved }) {
  const [f, setF] = useState({});
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setF(pet ? {
      name: pet.name, species: pet.species, breed: pet.breed || "", dob: pet.dob || "",
      gender: pet.gender || "unknown", weight_kg: pet.weight_kg || "",
      allergies: pet.allergies || "", food_preference: pet.food_preference || "",
      notes: pet.notes || "", photo_path: pet.photo_path || null,
    } : {
      name: "", species: "dog", breed: "", dob: "", gender: "unknown",
      weight_kg: "", allergies: "", food_preference: "", notes: "", photo_path: null,
    });
  }, [pet, open]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF(prev => ({ ...prev, photo_path: data.path }));
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const save = async () => {
    const payload = { ...f, customer_id: customerId, weight_kg: parseFloat(f.weight_kg) || null };
    try {
      if (pet) await api.patch(`/pets/${pet.id}`, payload);
      else await api.post("/pets", payload);
      toast.success("Pet saved");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="pet-dialog">
        <DialogHeader><DialogTitle>{pet ? "Edit" : "New"} pet</DialogTitle></DialogHeader>
        <div className="flex gap-4 items-start">
          <div>
            {f.photo_path ? (
              <img src={fileUrl(f.photo_path)} alt="" className="w-24 h-24 rounded-lg object-cover" />
            ) : (
              <div className="w-24 h-24 rounded-lg bg-muted grid place-items-center text-muted-foreground"><PawPrint /></div>
            )}
            <label className="mt-2 inline-flex items-center gap-1 text-xs text-primary cursor-pointer hover:underline">
              <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : "Photo"}
              <input type="file" accept="image/*" hidden onChange={upload} data-testid="pet-photo-upload" />
            </label>
          </div>
          <div className="flex-1 grid sm:grid-cols-2 gap-2">
            <Field label="Name *"><Input value={f.name || ""} onChange={e => setF(p => ({ ...p, name: e.target.value }))} data-testid="pet-name" /></Field>
            <Field label="Species">
              <Select value={f.species || "dog"} onValueChange={v => setF(p => ({ ...p, species: v }))}>
                <SelectTrigger data-testid="pet-species"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dog">Dog</SelectItem>
                  <SelectItem value="cat">Cat</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Breed"><Input value={f.breed || ""} onChange={e => setF(p => ({ ...p, breed: e.target.value }))} /></Field>
            <Field label="Gender">
              <Select value={f.gender || "unknown"} onValueChange={v => setF(p => ({ ...p, gender: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="DOB / Age date"><Input type="date" value={f.dob || ""} onChange={e => setF(p => ({ ...p, dob: e.target.value }))} /></Field>
            <Field label="Weight (kg)"><Input type="number" step="0.1" value={f.weight_kg || ""} onChange={e => setF(p => ({ ...p, weight_kg: e.target.value }))} /></Field>
            <Field label="Allergies" full><Input value={f.allergies || ""} onChange={e => setF(p => ({ ...p, allergies: e.target.value }))} /></Field>
            <Field label="Food preference" full><Input value={f.food_preference || ""} onChange={e => setF(p => ({ ...p, food_preference: e.target.value }))} /></Field>
            <Field label="Notes" full><Input value={f.notes || ""} onChange={e => setF(p => ({ ...p, notes: e.target.value }))} /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} data-testid="save-pet">Save pet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>
);

function VaccinationDialog({ open, onClose, pet }) {
  const [vacs, setVacs] = useState([]);
  const [f, setF] = useState({ vaccine_name: "", last_date: "", next_due_date: "", notes: "", document_path: null });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!pet) return;
    api.get(`/pets/${pet.id}`).then(r => setVacs(r.data.vaccinations || []));
  }, [pet]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF(prev => ({ ...prev, document_path: data.path }));
      toast.success("Document uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const add = async () => {
    if (!f.vaccine_name) { toast.error("Vaccine name required"); return; }
    try {
      await api.post("/vaccinations", { ...f, pet_id: pet.id, last_date: f.last_date || null, next_due_date: f.next_due_date || null });
      toast.success("Vaccination added");
      setF({ vaccine_name: "", last_date: "", next_due_date: "", notes: "", document_path: null });
      const r = await api.get(`/pets/${pet.id}`);
      setVacs(r.data.vaccinations || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remind = async (vid) => {
    try { await api.post(`/vaccinations/${vid}/remind`); toast.success("Reminder sent via WhatsApp"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (vid) => {
    try { await api.delete(`/vaccinations/${vid}`); setVacs(prev => prev.filter(v => v.id !== vid)); } catch { toast.error("Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="vacc-dialog">
        <DialogHeader><DialogTitle>Vaccinations · {pet?.name}</DialogTitle></DialogHeader>
        <div className="divide-y divide-border">
          {vacs.map(v => (
            <div key={v.id} className="py-3 flex items-center gap-3">
              <Syringe className="w-4 h-4 text-primary" />
              <div className="flex-1">
                <div className="font-semibold text-sm">{v.vaccine_name}</div>
                <div className="text-xs text-muted-foreground">Last: {v.last_date || "—"} · Next: {v.next_due_date || "—"}</div>
              </div>
              {v.document_path && <a href={fileUrl(v.document_path)} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Doc</a>}
              <Button size="sm" variant="ghost" onClick={() => remind(v.id)}><Send className="w-3 h-3 mr-1" />Remind</Button>
              <Button size="sm" variant="ghost" onClick={() => del(v.id)}><Trash2 className="w-3 h-3" /></Button>
            </div>
          ))}
          {vacs.length === 0 && <div className="text-sm text-muted-foreground py-3">No vaccinations recorded.</div>}
        </div>
        <div className="border-t border-border pt-3 grid sm:grid-cols-2 gap-2">
          <Field label="Vaccine *"><Input value={f.vaccine_name} onChange={e => setF(p => ({ ...p, vaccine_name: e.target.value }))} data-testid="vacc-name" /></Field>
          <Field label="Last date"><Input type="date" value={f.last_date} onChange={e => setF(p => ({ ...p, last_date: e.target.value }))} /></Field>
          <Field label="Next due"><Input type="date" value={f.next_due_date} onChange={e => setF(p => ({ ...p, next_due_date: e.target.value }))} data-testid="vacc-next" /></Field>
          <Field label="Notes"><Input value={f.notes} onChange={e => setF(p => ({ ...p, notes: e.target.value }))} /></Field>
          <div className="col-span-2 flex items-center gap-2">
            <label className="inline-flex items-center gap-1 text-xs text-primary cursor-pointer hover:underline">
              <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : f.document_path ? "Replace document" : "Attach document"}
              <input type="file" hidden onChange={upload} accept="image/*,application/pdf" data-testid="vacc-doc-upload" />
            </label>
            {f.document_path && <span className="text-xs text-muted-foreground">attached ✓</span>}
            <Button className="ml-auto" onClick={add} data-testid="add-vacc-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function CreditNotesPanel({ customerId, customer }) {
  const [summary, setSummary] = useState(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [settleFor, setSettleFor] = useState(null);

  const load = () => api.get(`/customers/${customerId}/credit-summary`).then(r => setSummary(r.data));
  useEffect(() => { load(); }, [customerId]); // eslint-disable-line

  const cancelCN = async (cn) => {
    if (!window.confirm(`Cancel credit note ${cn.credit_no}? Outstanding balance ₹${cn.balance} will be written off.`)) return;
    try {
      await api.delete(`/credit-notes/${cn.id}`);
      toast.success("Credit note cancelled");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!summary) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const outstanding = Number(summary?.outstanding || 0);
  const notes = Array.isArray(summary?.notes) ? summary.notes : [];

  return (
    <div className="space-y-3" data-testid="credit-notes-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-amber-900">Outstanding</div>
          <div className="font-heading text-2xl font-bold text-amber-900">₹{outstanding.toFixed(2)}</div>
        </div>
        <Button onClick={() => setIssueOpen(true)} data-testid="issue-credit-btn"><Plus className="w-4 h-4 mr-1" />Issue credit</Button>
      </div>

      <div className="divide-y divide-border rounded-md border border-border overflow-hidden">
        {notes.length === 0 && <div className="p-5 text-sm text-muted-foreground text-center">No credit notes yet.</div>}
        {notes.map(cn => (
          <div key={cn.id} className="p-3 flex items-center justify-between gap-3" data-testid={`credit-note-${cn.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono font-semibold">{cn.credit_no}</span>
                <Badge variant={cn.status === "settled" ? "secondary" : cn.status === "pending" ? "destructive" : cn.status === "partial" ? "default" : "outline"}>{cn.status}</Badge>
                {cn.due_date && <span className="text-xs text-muted-foreground">due {new Date(cn.due_date).toLocaleDateString()}</span>}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{cn.note || "—"} · issued {new Date(cn.created_at).toLocaleDateString()}</div>
            </div>
            <div className="text-right">
              <div className="text-sm">₹{Number(cn.paid_amount || 0).toFixed(2)} / ₹{Number(cn.amount || 0).toFixed(2)}</div>
              <div className="text-xs font-mono text-amber-700">Bal ₹{Number(cn.balance || 0).toFixed(2)}</div>
            </div>
            <div className="flex gap-1">
              {(cn.status === "pending" || cn.status === "partial") && (
                <Button variant="outline" size="sm" onClick={() => setSettleFor(cn)} data-testid={`settle-${cn.id}`}>Settle</Button>
              )}
              {(cn.status === "pending" || cn.status === "partial") && (
                <Button variant="ghost" size="sm" onClick={() => cancelCN(cn)} className="text-destructive" data-testid={`cancel-cn-${cn.id}`}><Trash2 className="w-4 h-4" /></Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <IssueCreditDialog open={issueOpen} onOpenChange={setIssueOpen} customer={customer} onSaved={load} />
      <SettleDialog cn={settleFor} onClose={() => { setSettleFor(null); load(); }} />
    </div>
  );
}

function IssueCreditDialog({ open, onOpenChange, customer, onSaved }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [due, setDue] = useState("");

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!customer.mobile && !customer.mobile_masked) { toast.error("Customer mobile is mandatory for credit notes"); return; }
    try {
      await api.post("/credit-notes", { customer_id: customer.id, amount: amt, note, due_date: due || null });
      toast.success("Credit note issued");
      setAmount(""); setNote(""); setDue("");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="issue-credit-dialog">
        <DialogHeader><DialogTitle>Issue credit to {customer.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Amount (₹) *</Label><Input type="number" value={amount} onChange={e => setAmount(e.target.value)} autoFocus data-testid="credit-amount" /></div>
          <div><Label>Note</Label><Input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Pay later for grooming" data-testid="credit-note" /></div>
          <div><Label>Due date (optional)</Label><Input type="date" value={due} onChange={e => setDue(e.target.value)} data-testid="credit-due-date" /></div>
          <div className="text-xs text-muted-foreground">Customer mobile <span className="font-mono">{customer.mobile_masked || customer.mobile}</span> will receive a WhatsApp reminder when settle is requested.</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="issue-credit-save">Issue</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettleDialog({ cn, onClose }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");

  useEffect(() => { if (cn) { setAmount(cn.balance.toFixed(2)); setMethod("cash"); setNote(""); } }, [cn]);

  if (!cn) return null;

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      await api.post(`/credit-notes/${cn.id}/settle`, { amount: amt, payment_method: method, note });
      toast.success(`Settled ₹${amt.toFixed(2)}`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={!!cn} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="settle-dialog">
        <DialogHeader><DialogTitle>Settle {cn.credit_no} · Outstanding ₹{cn.balance.toFixed(2)}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Amount received (₹) *</Label><Input type="number" value={amount} onChange={e => setAmount(e.target.value)} autoFocus data-testid="settle-amount" /></div>
          <div><Label>Payment method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="settle-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Note</Label><Input value={note} onChange={e => setNote(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="settle-save"><Send className="w-4 h-4 mr-1" />Receive payment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
